import type { TooltipOptions } from "highcharts";
import { renderToString } from "react-dom/server";

import { tooltipContainerConfig } from "@similarweb/pro-logic-components";
import { ChartTooltip } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";

import type { MetricKey, TimeGranularity } from "../../types";
import { METRIC_CONFIGURATION } from "../config";

import { formatTooltipTitle } from "./dateFormatters";

const getValueHeaderTitle = (metric: MetricKey, granularity: TimeGranularity) => {
    if (metric === "monthly-visits") {
        return translate("market.research.overview.comparison.highlevel.chart.tooltip.monthlyVisits", {
            granularity: granularity,
        });
    }
    return METRIC_CONFIGURATION[metric].metricName;
};

export const getTooltipConfig = (metric: MetricKey, granularity: TimeGranularity): TooltipOptions => {
    return {
        ...tooltipContainerConfig().tooltip,
        shared: true,
        outside: false,
        useHTML: true,
        formatter: function () {
            const formatter = METRIC_CONFIGURATION[metric].tooltipFormatter;
            const title = formatTooltipTitle(this.x, granularity);

            const data = this.points.map((point) => ({
                category: {
                    categoryName: point.series.name,
                    color: point.series.color as string,
                },
                value: formatter(point.y),
            }));

            return renderToString(
                <ChartTooltip
                    width={260}
                    title={title}
                    data={data}
                    columnDefinition={[
                        {
                            align: "left",
                            key: "category",
                            size: "2fr",
                            title: translate("common.industry"),
                            selector: "category",
                            render: ({ category }) => (
                                <ChartTooltip.LabelCell
                                    data-automation="overview-graph-tooltip-category-name"
                                    label={category.categoryName}
                                    backgroundColor={category.color}
                                />
                            ),
                        },
                        {
                            align: "right",
                            key: "value",
                            size: "1fr",
                            title: getValueHeaderTitle(metric, granularity),
                            selector: "value",
                            render: ({ value }) => (
                                <ChartTooltip.ValueCell
                                    data-automation="overview-graph-tooltip-value"
                                    value={value}
                                />
                            ),
                        },
                    ]}
                />,
            );
        },
    };
};
